import { ChevronDownIcon, WarningIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import BigNumber from "bignumber.js";
import { ethers } from "ethers";
import { useFormik } from "formik";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { MINT_TYPE, NFT_TYPE } from "../../../core/constants/nft";
import { SELLER_STATUS } from "../../../core/constants/status";
import { LAYER_1_TOKENS } from "../../../core/constants/token";
import { useMarketplaceAddress } from "../../../core/hooks/useAddress";
import { useERC1155BePayContract, useERC721Contract, useHelperContract } from "../../../core/hooks/useContract";
import { IMoralisNft } from "../../../core/models/nft";
import { Quote, signQuote } from "../../../core/models/quote";
import { IUser } from "../../../core/models/user";
import { getSalt } from "../../../core/utils/getSalt";
import { formatNumber, toPlainString } from "../../../core/utils/number";
import { quoteService } from "../../../services/quote.service";
import { useAppSelector } from "../../../store/hook";
import { CompleteListingModal } from "./CompleteListingModal";
import { SellerStatusWarningModal } from "./SellerStatusWarningModal";

enum LISTING_STATE {
  COMFIRMING,
  NONE,
  NOT_VERIFIED,
}

interface Props {
  nft: IMoralisNft | undefined;
  owners: { user: IUser; quantityOwned: string }[];
  onSuccess: () => void;
}

export const ListingForm = React.memo(({ nft, onSuccess, owners }: Props) => {
  const { account, library } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const marketplaceAddress = useMarketplaceAddress();
  const helperContract = useHelperContract();

  const erc721Contract = useERC721Contract(nft?.token_address);
  const erc1155Contract = useERC1155BePayContract(nft?.token_address);

  const [isConfirmingListing, setIsConfirmingListing] = useState(false);
  const [listingState, setListingState] = useState<LISTING_STATE>(LISTING_STATE.NONE);
  const [confirmError, setConfirmError] = useState<string>();

  const owner = useMemo(() => owners.find((o) => o.user.id?.toLowerCase() === account?.toLowerCase()), [owners]);
  const quantityOwnedByAccount = useMemo(() => owner?.quantityOwned, [owner]);

  const listingSchema = yup.object({
    quantity: yup
      .number()
      .required(t("Quantity is required"))
      .integer(t("Quantity must be integer"))
      .min(1, t("Quantity must be greater than 0"))
      .max(
        parseInt(quantityOwnedByAccount || "1"),
        `${t("Quantity must be less than or equal to")} ${quantityOwnedByAccount}`
      ),
    price: yup
      .number()
      .required(t("Price is required"))
      .min(0, t("Price must greater than 0"))
      .max(Number.MAX_SAFE_INTEGER, t("Price must be a safe number"))
      .test(
        "length",
        (_) => t("Price must be a safe number"),
        (currentValue) => {
          if (!currentValue) return true;
          return toPlainString(currentValue).replace(".", "").length < 17;
        }
      )
      .test({
        name: "decimal",
        params: { decimals: yup.ref("token.decimals") },
        message: t("Decimal places must be less than or equal to ${decimals} numbers"),
        test: (currentValue, context) => {
          if (!currentValue) return true;
          const [_, fraction] = toPlainString(currentValue)?.split(".");
          const { token } = context.parent;
          return !fraction || fraction.length <= token.decimals;
        },
      }),
  });

  const formik = useFormik({
    initialValues: {
      price: undefined,
      quantity: 1,
      token: supportedTokens.byId[supportedTokens.allIds[0]],
    },
    validationSchema: listingSchema,
    onSubmit: async (values) => {
      if (!values.price || !values.quantity) return;

      if (owner?.user.seller_status === SELLER_STATUS.VERIFIED) {
        setListingState(LISTING_STATE.COMFIRMING);
      } else {
        setListingState(LISTING_STATE.NOT_VERIFIED);
      }
    },
  });

  const onConfirmListing = async () => {
    try {
      setIsConfirmingListing(true);
      const { price, quantity, token } = formik.values;
      if (!price || !account || !library || !nft?.token_address) return;

      let isMinted = false;

      //Check if NFT is minted or not
      try {
        if (nft.contract_type === NFT_TYPE.ERC_721) {
          const ownerAddress = await erc721Contract?.ownerOf(nft.token_id);
          isMinted = !!ownerAddress;
        } else {
          isMinted = true;
        }
      } catch (e) {
        isMinted = false;
      }

      const priceBN = new BigNumber(price);
      const isLayer1Token = LAYER_1_TOKENS.includes(token.underlying);
      const quote: Quote = {
        maker: account.toLowerCase(),
        salt: getSalt(),
        make: {
          asset_type: {
            asset_class: !isMinted ? `${nft?.contract_type || "ERC721"}_LAZY` : nft?.contract_type || "ERC721",
            token_id: nft?.token_id,
            token_address: nft?.token_address,
            ...(!isMinted && {
              creators: nft.creators,
              royalties: nft.royalties,
              token_uri: nft.token_uri,
              signatures: nft.signatures,
              supply: quantity,
            }),
          },
          value: Math.floor(quantity),
        },
        taker: ethers.constants.AddressZero,
        take: {
          asset_type: {
            asset_class: isLayer1Token ? "ETH" : "ERC20",
            token_address: token.address,
          },
          value: Math.floor(
            priceBN.multipliedBy(Math.pow(10, token.decimals)).multipliedBy(formik.values.quantity).toNumber()
          ),
        },
        sale_method: 0,
        maker_type: 0,
        data_type: "V1",
        data: {
          is_make_fill: true,
          payouts: [],
          origin_fees: owner?.user.commission_fee || 0,
        },
      };

      const onChainQuote = await Quote.toOnChainQuote(quote, helperContract);
      const signature = await signQuote(library, onChainQuote, account, marketplaceAddress);

      let tokenName;
      let symbol;

      if (nft.contract_type === NFT_TYPE.ERC_721) {
        tokenName = await erc721Contract?.name();
        symbol = await erc721Contract?.symbol();
      }

      await quoteService.create({
        ...quote,
        signature,
        nft: {
          attributes: (nft?.metadata?.attributes || []).map((a) => ({
            ...a,
            value: a.value?.toString(),
            trait_type: a.trait_type?.toString(),
          })),
          description: nft?.metadata?.description,
          image: nft?.metadata?.image || nft?.metadata?.animation_url,
          name: nft?.metadata?.name || "",
          token_name: tokenName || "",
          symbol: symbol || "",
          value: quantity,
          token_address: nft?.token_address as string,
          token_id: nft?.token_id as string,
          token_uri: nft?.token_uri,
          b_token_address: formik.values.token.address,
          price,
          contract_type: nft.contract_type,
          owner_of: account.toLowerCase(),
          mint_type: nft.mint_type,
        },
        make_asset_data: onChainQuote.makeAsset.assetType.data,
        take_asset_data: onChainQuote.takeAsset.assetType.data,
        data_encode: onChainQuote.data,
      });

      onSuccess();
    } catch (e: any) {
      setConfirmError(e?.message);
    } finally {
      setIsConfirmingListing(false);
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={7}>
          <Stack>
            <Heading size="md">{t("Price")}</Heading>
            <Box>
              <Menu>
                <InputGroup>
                  <InputLeftAddon width={"7.2rem"} as={MenuButton} type="button">
                    <HStack alignItems="center" spacing={1}>
                      <Image src={formik.values.token?.logoUrl} height="20px" width="20px" />
                      <Text color="#666666" fontWeight="700">
                        {formik.values.token?.symbol}
                      </Text>
                      <ChevronDownIcon color="#666666" />
                    </HStack>
                  </InputLeftAddon>
                  <Input
                    type="number"
                    value={formik.values.price === undefined ? "" : formik.values.price}
                    name="price"
                    onChange={formik.handleChange}
                    placeholder={t("Enter item price for one piece")}
                    fontWeight="700"
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                {!!formik.errors.price && formik.touched.price && (
                  <Text color={"red.500"} fontSize={"small"} marginTop={1}>
                    {formik.errors.price}
                  </Text>
                )}

                <MenuList minWidth="240px">
                  <MenuOptionGroup
                    value={formik.values.token.address}
                    title="Tokens"
                    type="radio"
                    onChange={(value) => {
                      if (typeof value !== "string") return;
                      formik.setFieldValue("token", supportedTokens.byId[value]);
                    }}
                  >
                    {supportedTokens.allIds.map((id) => {
                      const supportedToken = supportedTokens.byId[id];
                      return (
                        <MenuItemOption value={supportedToken.address} key={id}>
                          <Flex alignItems="center">
                            <Image src={supportedToken?.logoUrl} height="20px" width="20px" marginRight="8px" />
                            <Text color="#666666" fontWeight="700">
                              {supportedToken?.symbol}
                            </Text>
                          </Flex>
                        </MenuItemOption>
                      );
                    })}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </Box>
          </Stack>

          <Box>
            <Flex>
              <Heading size="md">{t("Fee")}</Heading>
              <Tooltip
                hasArrow
                label={t("Once sold, the following fees will be deducted.")}
                borderRadius="lg"
                padding="6px"
                placement="top"
              >
                <WarningIcon w={4} h={4} color={colors.primary} marginLeft="6px" />
              </Tooltip>
            </Flex>

            <Box marginTop="8px">
              {!!owner?.user.commission_fee && (
                <Box fontWeight="500" color="#434F64">
                  {t("Service fee")}:{" "}
                  <b>{formatNumber((owner.user.commission_fee / 1e18) * 100, { maximumFractionDigits: 2 })}%</b>
                </Box>
              )}
              <Box fontWeight="500" color="#434F64">
                {t("Buy Now Pay Later fee")}: <b>{formatNumber(2.5, { maximumFractionDigits: 2 })}%</b>
              </Box>
            </Box>
          </Box>

          {nft?.contract_type === NFT_TYPE.ERC_1155 && (
            <Stack>
              <Heading size="md">{t("Number of copies")}</Heading>
              <Box>
                <Input
                  placeholder={t("E.g: 10")}
                  value={formik.values.quantity}
                  name="quantity"
                  type={"number"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                {!!formik.errors.quantity && formik.touched.quantity && (
                  <Text color={"red.500"} fontSize={"small"} marginTop={1}>
                    {formik.errors.quantity}
                  </Text>
                )}
              </Box>
            </Stack>
          )}
        </Stack>

        <Box padding="24px 0px">
          <Divider />
        </Box>

        <Button colorScheme="blue" width="150px" type="submit">
          {t("List")}
        </Button>
      </form>
      {!!nft && (
        <CompleteListingModal
          isVisible={listingState === LISTING_STATE.COMFIRMING}
          onClose={() => {
            setListingState(LISTING_STATE.NONE);
          }}
          nft={nft}
          price={formik.values.price || 0}
          token={formik.values.token}
          onConfirm={onConfirmListing}
          isConfirming={isConfirmingListing}
          confirmError={confirmError}
          quantity={formik.values.quantity}
        />
      )}
      <SellerStatusWarningModal
        isVisible={listingState === LISTING_STATE.NOT_VERIFIED}
        onClose={() => {
          setListingState(LISTING_STATE.NONE);
        }}
      />
    </>
  );
});
